'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import cx from 'classnames';
import Balancer from 'react-wrap-balancer';
import Link from 'next/link';

import { Text } from './Text';
import { Button } from './Button';
import { TextInput } from './TextInput';
import { Form, FormSection, FormField } from './Form';
import { GoogleLogo } from './GoogleLogo';
import { useValidatedState } from '../hooks/validated-state';
import * as analytics from '../lib/analytics';
import { Column, Row, Section } from './Section';
import { aggregateReviews } from '../lib/reviewAggregation';
import { Rating } from './Rating';

import bcbsncLogo from '../public/static/images/bcbsnc.svg';
import phoneIcon from '../public/static/images/phone.svg';

interface HeroProps {
  heading: string;
  subheading: string;
  image: {
    url: string;
    description: string;
  };
  defaultPlanTypeOption?: 'individual' | 'medicare' | 'dental' | 'group';
  aggregatedReviews:
    | (Omit<ReturnType<typeof aggregateReviews>, 'total'> & {
        total: string | number;
      })
    | undefined;
}

export const Hero: React.FC<HeroProps> = ({
  heading,
  subheading,
  image,
  defaultPlanTypeOption = 'individual',
  aggregatedReviews,
}) => {
  const [planType, setPlanType] = useState(defaultPlanTypeOption);

  return (
    <>
      <Section borderless background="BLUE" className="relative !py-4">
        <div className="hidden md:inline">
          <Image
            src={image.url}
            alt={image.description}
            fill={true}
            sizes="100vw"
            className={cx(
              'object-contain object-center md:object-right-bottom max-h-44 md:max-h-none md:h-full md:absolute md:inset-0',
            )}
            priority
          />
        </div>
        <Row className="items-center">
          <Column className="z-10">
            <div className="text-center md:text-left mx-auto md:max-w-none space-y-2 md:space-y-0">
              <Text.H1 className="!text-2xl md:!text-4xl font-semibold md:font-bold !leading-tight md:leading-snug md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl">
                <Balancer>{heading}</Balancer>
              </Text.H1>
              <Text.Body className="text-sm md:text-base !text-gray-500 font-medium">
                {subheading}
              </Text.Body>
            </div>
            <div className="rounded-lg bg-white drop-shadow-lg max-w-xl !mb-6">
              <div className="md:hidden">
                <Image
                  src={image.url}
                  alt={image.description}
                  fill={true}
                  sizes="100vw"
                  className={cx(
                    '!static object-contain object-center md:object-right-bottom max-h-44 md:max-h-none md:h-full md:absolute md:inset-0',
                  )}
                  priority
                />
              </div>
              {planType !== 'group' && (
                <nav
                  className="isolate flex divide-x divide-gray-200 rounded-lg border border-gray-100 md:border-none shadow mx-4 md:mx-0"
                  aria-label="Tabs"
                >
                  {[
                    { name: 'Individual & Family', key: 'individual' as const },
                    { name: 'Medicare', key: 'medicare' as const },
                    { name: 'Dental', key: 'dental' as const },
                  ].map(({ name, key }, tabIdx, tabs) => {
                    const current = key === planType;
                    return (
                      <button
                        key={key}
                        className={cx(
                          current
                            ? 'text-gray-900'
                            : 'text-gray-500 hover:text-gray-700',
                          tabIdx === 0 ? 'rounded-l-lg md:rounded-bl-none' : '',
                          tabIdx === tabs.length - 1
                            ? 'rounded-r-lg md:rounded-br-none'
                            : '',
                          'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10',
                        )}
                        aria-current={key === planType ? 'page' : undefined}
                        onClick={() => setPlanType(key)}
                      >
                        <span>{name}</span>
                        <span
                          aria-hidden="true"
                          className={cx(
                            key === planType ? 'bg-blue-500' : 'bg-transparent',
                            'absolute inset-x-0 bottom-0 h-0.5',
                          )}
                        />
                      </button>
                    );
                  })}
                </nav>
              )}
              <HeroForm planType={planType} />
              <div className="pb-4 px-4 md:pb-5 md:px-6 flex justify-center md:justify-start">
                <a
                  href="tel:1-800-797-0327"
                  className="flex flex-col justify-center md:justify-start"
                >
                  <div className="text-xs md:text-sm flex justify-center md:justify-start">
                    <span className="hidden sm:inline">
                      North Carolina Based&nbsp;
                    </span>
                    Agents Available 24/7
                  </div>
                  <div className="text-sm md:text-xl font-semibold whitespace-no-wrap flex justify-center md:justify-start items-center">
                    <Image
                      src={phoneIcon}
                      className="mr-2 h-3 md:h-5"
                      alt=""
                      priority
                    />{' '}
                    <span>Call 800-797-0327</span>
                  </div>
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1 justify-items-center items-center max-w-xl">
              <BlueCrossAuthorizedAgencyLogo />
              {aggregatedReviews ? (
                <Link href="#reviews">
                  <div className="flex items-center gap-1">
                    <GoogleLogo />
                    <div className="-mt-1.5">
                      <Rating
                        stars={aggregatedReviews.average}
                        className="w-5"
                      />
                    </div>
                  </div>
                  <p className="text-gray-700 text-xs sm:text-sm font-semibold">
                    <span className="inline-block">
                      {aggregatedReviews.total}
                    </span>{' '}
                    <span className="inline-block">Google Reviews</span>
                  </p>
                </Link>
              ) : null}
            </div>
          </Column>
        </Row>
      </Section>
    </>
  );
};

interface HeroFormProps {
  planType: 'individual' | 'medicare' | 'dental' | 'group';
}

const HeroForm: React.FC<HeroFormProps> = ({ planType }) => {
  const router = useRouter();

  const [
    zipCode,
    setZipCode,
    isZipCodeValid,
    shouldIndicateInvalidZipCode,
    setShouldIndicateInvalidZipCode,
  ] = useValidatedState(
    '',
    (zipCode) =>
      Boolean((zipCode || '').match(/^[0-9]{5}$/)) &&
      // Verify Zip Code is in North Carolina
      Number(zipCode) >= 27000 &&
      Number(zipCode) <= 28999,
  );

  const validationStates = [isZipCodeValid];

  const makeAllValidationErrorsVisible = () => {
    setShouldIndicateInvalidZipCode(true);
  };
  return (
    <Form
      onSubmit={() => {
        if (validationStates.some((isValid) => !isValid)) {
          makeAllValidationErrorsVisible();

          return;
        }

        analytics.logEvent({
          category: 'Hero',
          name: 'Quote form submitted',
          includesZip: !!zipCode,
        });

        router.push(
          `/quote?${new URLSearchParams({
            zipCode: zipCode,
            planType: planType,
          })}`,
        );
      }}
      className="py-2 md:!py-4"
    >
      <FormSection>
        <div className="flex flex-col md:flex-row md:items-start md:space-x-4">
          <FormField
            error={
              shouldIndicateInvalidZipCode
                ? 'Please enter a valid North Carolina ZIP Code.'
                : undefined
            }
          >
            <TextInput
              value={zipCode}
              onChange={setZipCode}
              maxLength={5}
              inputMode="numeric"
              placeholder="Enter your ZIP Code"
            />
          </FormField>
          <Button
            variant="PRIMARY"
            size="EXTRA_LARGE"
            is="input"
            type="submit"
            value={
              planType === 'individual'
                ? 'Find Healthcare Plans'
                : planType === 'medicare'
                ? 'Find Medicare Plans'
                : planType === 'dental'
                ? 'Find Dental Plans'
                : 'Find Plans'
            }
          />
        </div>
      </FormSection>
    </Form>
  );
};

const BlueCrossAuthorizedAgencyLogo: React.FC = () => (
  <Image
    src={bcbsncLogo}
    className="h-10 lg:h-12"
    alt="Blue Cross NC Authorized Agency"
    priority
  />
);
